import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import MentorshipMenteePoolFileUpload from './MentorshipMenteePoolFileUpload';
import MentorshipMenteePoolManualAdd from './MentorshipMenteePoolManualAdd';
import MentorshipQuickMenteeProfile from './MentorshipQuickMenteeProfile';
import MentorshipService, {
    EligibleProgramSelection,
    MENTEE_POOL_CONFIRMATION_STATUSES,
    MENTEE_POOL_PAGE_SIZE_DEFAULT,
    MENTEE_POOL_PAGE_SIZES,
    MenteePoolBulkAction,
    MentorshipCampusConfig,
    MentorshipCampusOption,
    MentorshipMenteePoolListQuery,
    MentorshipMenteePoolMember,
    MentorshipProgramOption,
    MentorshipTaxonomyTrack,
} from './MentorshipService';

const POOL_MODAL_OVER_OFFCANVAS = { zIndex: 10620 };

const selectedPositiveIds = (event: React.ChangeEvent<HTMLSelectElement>) =>
    Array.from(event.target.selectedOptions, (option) => option.value)
        .filter((value) => value !== '')
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

interface MentorshipMenteePoolProps {
    cohortId: number | null;
    disabled?: boolean;
    campuses: MentorshipCampusOption[];
    isGroup?: boolean;
    programs?: MentorshipProgramOption[];
    eligiblePrograms?: EligibleProgramSelection[];
    campusConfigs?: MentorshipCampusConfig[];
    /** Cohort `end_date` (YYYY-MM-DD) for honest subtitle — never invent a window. */
    cohortEndDate?: string | null;
    /** Notify parent Module Settings to refresh pill badge totals after add/remove. */
    onPoolMembershipChange?: () => void;
    /**
     * Switch Module Settings to Campus Configuration, where mentee eligibility (eligible student
     * programs + mentee mode) is configured. Without it the button falls back to the old
     * open-in-new-tab behaviour.
     */
    onOpenEligibilitySettings?: () => void;
}

const formatCohortEndLabel = (endDate?: string | null): string | null => {
    const raw = String(endDate || '').trim().slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const emptyQuery = (): MentorshipMenteePoolListQuery => ({
    search: '',
    campusId: null,
    confirmationStatus: [],
    trackIds: [],
    programIds: [],
    years: [],
    page: 1,
    pageSize: MENTEE_POOL_PAGE_SIZE_DEFAULT,
});

const toggleNumber = (values: number[], value: number) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const toggleString = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const MentorshipMenteePool = ({
    cohortId,
    disabled,
    campuses,
    isGroup,
    programs = [],
    eligiblePrograms = [],
    campusConfigs = [],
    cohortEndDate = null,
    onPoolMembershipChange,
    onOpenEligibilitySettings,
}: MentorshipMenteePoolProps) => {
    const [draft, setDraft] = useState<MentorshipMenteePoolListQuery>(emptyQuery());
    const [query, setQuery] = useState<MentorshipMenteePoolListQuery>(emptyQuery());
    const [members, setMembers] = useState<MentorshipMenteePoolMember[]>([]);
    const [tracks, setTracks] = useState<MentorshipTaxonomyTrack[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(MENTEE_POOL_PAGE_SIZE_DEFAULT);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [showManualAdd, setShowManualAdd] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [trackMember, setTrackMember] = useState<MentorshipMenteePoolMember | null>(null);
    const [statusMember, setStatusMember] = useState<MentorshipMenteePoolMember | null>(null);
    const [removeMember, setRemoveMember] = useState<MentorshipMenteePoolMember | null>(null);
    const [trackIds, setTrackIds] = useState<number[]>([]);
    const [statusValue, setStatusValue] = useState('');
    const [statusComment, setStatusComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [profileMember, setProfileMember] = useState<MentorshipMenteePoolMember | null>(null);
    const [showBulkRemove, setShowBulkRemove] = useState(false);
    const [registeredTotal, setRegisteredTotal] = useState<number | null>(null);
    const [yetToRegisterTotal, setYetToRegisterTotal] = useState<number | null>(null);

    const cohortAvailable = cohortId != null && !disabled;
    const poolSummaryLine = useMemo(() => {
        const parts: string[] = [];
        if (registeredTotal != null) parts.push(`${registeredTotal} Registered`);
        if (yetToRegisterTotal != null) parts.push(`${yetToRegisterTotal} Yet to register`);
        const endLabel = formatCohortEndLabel(cohortEndDate);
        if (endLabel) parts.push(`Cohort ends ${endLabel}`);
        return parts.length > 0 ? parts.join(' · ') : null;
    }, [registeredTotal, yetToRegisterTotal, cohortEndDate]);
    const yearOptions = useMemo(() => MentorshipService.menteePoolPassOutYearOptions(), []);
    const uniquePrograms = useMemo(() => {
        const seen = new Set<number>();
        const items: Array<{ programId: number; name: string }> = [];
        for (const program of programs) {
            if (seen.has(program.programId)) continue;
            seen.add(program.programId);
            items.push({ programId: program.programId, name: program.name });
        }
        return items;
    }, [programs]);

    const loadTracks = async (id: number) => {
        const taxonomy = await MentorshipService.getDomainTaxonomy(id);
        setTracks(taxonomy.tracks || []);
    };

    const loadList = async (id: number, nextQuery: MentorshipMenteePoolListQuery) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const result = await MentorshipService.listMenteePool(id, nextQuery);
            setMembers(result.members);
            setPage(result.pagination.page);
            setPageSize(result.pagination.pageSize);
            setTotal(result.pagination.total);
            setTotalPages(result.pagination.totalPages);
            setSelectedIds([]);
        } catch (error) {
            setMembers([]);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const loadStatusSummary = async (id: number) => {
        try {
            const [registered, yetToRegister] = await Promise.all([
                MentorshipService.listMenteePool(id, {
                    confirmationStatus: ['Registered'],
                    page: 1,
                    pageSize: 1,
                }),
                MentorshipService.listMenteePool(id, {
                    confirmationStatus: ['Yet to register'],
                    page: 1,
                    pageSize: 1,
                }),
            ]);
            setRegisteredTotal(registered.pagination.total);
            setYetToRegisterTotal(yetToRegister.pagination.total);
        } catch {
            setRegisteredTotal(null);
            setYetToRegisterTotal(null);
        }
    };

    useEffect(() => {
        if (cohortId == null || disabled) {
            setMembers([]);
            setTracks([]);
            setSelectedIds([]);
            setProfileMember(null);
            setShowBulkRemove(false);
            setInfoMessage('');
            setDraft(emptyQuery());
            setQuery(emptyQuery());
            setTotal(0);
            setTotalPages(0);
            setShowManualAdd(false);
            setTrackMember(null);
            setStatusMember(null);
            setRemoveMember(null);
            setRegisteredTotal(null);
            setYetToRegisterTotal(null);
            return;
        }
        const next = { ...emptyQuery() };
        setDraft(next);
        setQuery(next);
        setProfileMember(null);
        loadTracks(cohortId).catch((error) => setErrorMessage(MentorshipService.getErrorMessage(error)));
        loadList(cohortId, next);
        loadStatusSummary(cohortId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, disabled]);

    const applyDraftFilters = () => {
        if (cohortId == null) return;
        const next = { ...draft, page: 1, pageSize };
        setQuery(next);
        loadList(cohortId, next);
    };

    const loadPage = (nextPage: number, nextPageSize = pageSize) => {
        if (cohortId == null) return;
        const next = { ...query, page: nextPage, pageSize: nextPageSize };
        setQuery(next);
        loadList(cohortId, next);
    };

    const refresh = () => {
        if (cohortId == null) return;
        loadList(cohortId, query);
        loadStatusSummary(cohortId);
        onPoolMembershipChange?.();
    };

    const visibleIds = useMemo(() => members.map((item) => item.id), [members]);
    const selectedCount = useMemo(
        () => MentorshipService.uniqueMenteePoolMemberIds(selectedIds).length,
        [selectedIds]
    );
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    const toggleVisible = () => {
        if (allVisibleSelected) {
            setSelectedIds((current) => current.filter((id) => !visibleIds.includes(id)));
            return;
        }
        setSelectedIds((current) => Array.from(new Set([...current, ...visibleIds])));
    };

    const allowOptOutFor = (campusId: number | null) =>
        campusConfigs.some((config) => config.campusId === campusId && config.allowOptOutMidCohort);

    const saveTracks = async () => {
        if (cohortId == null || trackMember == null) return;
        if (trackIds.length < 1) {
            setErrorMessage('Select at least one career track.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.replaceMenteePoolMemberTracks(cohortId, trackMember.id, trackIds);
            const nextTracks = tracks
                .filter((track) => track.id != null && trackIds.includes(track.id as number))
                .map((track) => ({ id: track.id as number, name: track.name ?? null }));
            setProfileMember((current) =>
                current?.id === trackMember.id ? { ...current, tracks: nextTracks } : current
            );
            setTrackMember(null);
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const saveStatus = async () => {
        if (cohortId == null || statusMember == null || !statusValue) return;
        if (statusValue === 'Opted-out' && !allowOptOutFor(statusMember.campusId)) {
            setErrorMessage('Unsupported value');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.updateMenteePoolMemberStatus(
                cohortId,
                statusMember.id,
                statusValue,
                statusComment.trim() === '' ? null : statusComment
            );
            setProfileMember((current) =>
                current?.id === statusMember.id
                    ? {
                          ...current,
                          confirmationStatus: statusValue,
                          confirmationComment: statusComment.trim() === '' ? null : statusComment,
                      }
                    : current
            );
            setStatusMember(null);
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const confirmRemove = async () => {
        if (cohortId == null || removeMember == null) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.removeMenteePoolMember(cohortId, removeMember.id);
            setRemoveMember(null);
            setProfileMember((current) => (current?.id === removeMember.id ? null : current));
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const sendMessage = (targetMembers: MentorshipMenteePoolMember[]) => {
        const result = MentorshipService.openMenteePoolMessages(targetMembers);
        if (!result.success) {
            setErrorMessage(result.message);
            return;
        }
        setErrorMessage('');
        setInfoMessage('');
    };

    const handleRowAction = (memberId: number, action: string) => {
        const member = members.find((item) => item.id === memberId);
        if (!member) return;
        switch (action) {
            case 'VIEW':
                setProfileMember(member);
                break;
            case 'MESSAGE':
                sendMessage([member]);
                break;
            case 'STATUS':
                setStatusMember(member);
                setStatusValue(member.confirmationStatus || 'Yet to register');
                setStatusComment(member.confirmationComment || '');
                break;
            case 'REMOVE':
                setRemoveMember(member);
                break;
            default:
                break;
        }
    };

    const runBulk = async (action: MenteePoolBulkAction) => {
        if (cohortId == null) return;
        const memberIds = MentorshipService.uniqueMenteePoolMemberIds(selectedIds);
        const capError = MentorshipService.menteePoolBulkCapError(action, memberIds);
        if (capError) {
            setErrorMessage(capError);
            setInfoMessage('');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        setInfoMessage('');
        try {
            const result = await MentorshipService.bulkMenteePoolActions(cohortId, action, memberIds);
            setInfoMessage(MentorshipService.formatMenteePoolBulkResult(result));
            if (action === 'remove') {
                const removed = new Set(
                    (result.processed || [])
                        .map((item) => item.memberId)
                        .filter((id): id is number => id != null)
                );
                setProfileMember((current) => (current && removed.has(current.id) ? null : current));
                setShowBulkRemove(false);
            }
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-4 gap-3">
                <div>
                    <div className="font-20 font-600 mb-0">Mentee Pool</div>
                    {poolSummaryLine ? (
                        <div className="font-13 text-secondary">{poolSummaryLine}</div>
                    ) : null}
                </div>
                
                <div className="d-flex gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!cohortAvailable}
                        onClick={() =>
                            onOpenEligibilitySettings
                                ? onOpenEligibilitySettings()
                                : MentorshipService.openEligibilitySettings()
                        }>
                        Eligibility Settings
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!cohortAvailable}
                        onClick={() => setShowFileUpload(true)}>
                        File Upload
                    </button>
                    <button
                        type="button"
                        className="btn btn-green btn-sm font-13 font-600 py-2 px-3 "
                        disabled={!cohortAvailable}
                        onClick={() => setShowManualAdd(true)}>
                        <span>+ Add Manually</span>
                    </button>
                    {/* Send Message hidden from the Mentee Pool header (QA). Row-level Message was
                        already hidden in R-QA-01 intake 33; the helper stays for Quick Profile. */}
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!cohortAvailable || selectedIds.length === 0 || isSaving}
                        onClick={() => runBulk('registration-link')}>
                        Send Registration Link
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!cohortAvailable || selectedIds.length === 0 || isSaving}
                        onClick={() => runBulk('reminder')}>
                        Send Reminder to register
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-danger btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={!cohortAvailable || selectedIds.length === 0 || isSaving}
                        onClick={() => setShowBulkRemove(true)}>
                        Remove from Pool
                    </button>
                </div>
            </div>

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to manage Mentee Pool.</div>}
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {infoMessage && <div className="alert alert-info">{infoMessage}</div>}

            <fieldset disabled={!cohortAvailable}>
                <div className="card border-0 rounded-3 p-3 shadow-sm bg-white mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                            <div className="position-relative" style={{minWidth: 220}}>                                
                                <input
                                    className="form-control form-search font-14 border-light-subtle"
                                    placeholder="Search name, email, or UUID"
                                    value={draft.search || ''}
                                    onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            applyDraftFilters();
                                        }
                                    }}
                                />
                            </div>

                            {isGroup && (
                                <select
                                    className="form-select font-13 w-auto"
                                    value={draft.campusId ?? ''}
                                    onChange={(event) =>
                                        setDraft((current) => ({
                                            ...current,
                                            campusId: event.target.value ? Number(event.target.value) : null,
                                        }))
                                    }>
                                    <option value="">All campuses</option>
                                    {campuses.map((campus) => (
                                        <option key={campus.value} value={campus.value}>
                                            {campus.label}
                                        </option>
                                    ))}
                                </select>
                                    
                                )}

                                {uniquePrograms.length > 0 && (
                                    
                                        <select
                                            className="form-select font-13 w-auto"
                                            value={(draft.programIds || []).map(String)}
                                            onChange={(e) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    programIds: selectedPositiveIds(e),
                                                }))
                                            }
                                        >   <option value="">Program</option>
                                            {uniquePrograms.map((program) => (
                                                <option key={program.programId} value={program.programId}>
                                                    {program.name}
                                                </option>
                                            ))}
                                        </select>
                                )}

                                <select
                                    className="form-select font-13 w-auto"
                                    value={(draft.years || [])[0] ?? ''}
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        const year = raw ? Number(raw) : null;
                                        setDraft((current) => ({
                                            ...current,
                                            years:
                                                year != null && Number.isFinite(year) ? [year] : [],
                                        }));
                                    }}>
                                    <option value="">Select year</option>
                                    {yearOptions.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="form-select font-13 w-auto"
                                    value={draft.confirmationStatus || []}
                                    onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                                        setDraft((current) => ({
                                            ...current,
                                            confirmationStatus: selected,
                                        }));
                                    }}
                                > <option value="">Status</option>
                                    {MENTEE_POOL_CONFIRMATION_STATUSES.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="form-select font-13 w-auto"
                                    value={(draft.trackIds || []).map(String)}
                                    onChange={(e) => {
                                        setDraft((current) => ({
                                            ...current,
                                            trackIds: selectedPositiveIds(e),
                                        }));
                                    }}
                                ><option value="">Career Tracks</option>
                                    {tracks
                                        .filter((track) => track.id != null)
                                        .map((track) => (
                                            <option key={track.id} value={track.id as number}>
                                                {track.name}
                                            </option>
                                        ))}
                                </select>

                                <button type="button" className="btn btn-green btn-sm" onClick={applyDraftFilters}>
                                    <span>Apply filters</span>
                                </button>

                        </div>
                    </div>
                </div>
               
            </fieldset>

            {isLoading ? (
                <div className="text-muted">Loading Mentee Pool…</div>
            ) : members.length === 0 ? (
                <div className="text-muted">No mentees in this pool yet.</div>
            ) : (
                <>
                    <div className="card border-0 rounded-3 shadow-sm bg-white overflow-hidden">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0 table-hover" style={{ minWidth: 1360 }}>
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 bg-light" style={{ width: 40 }}>
                                            <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} />
                                        </th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Student Name</th>
                                        {/* UUID column hidden in Mentee Pool UI. Keep the field on the row for search and profile links. */}
                                        {/* <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">UUID</th> */}
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Program</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Year</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Career Tracks</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Preferred Mentorship Areas</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Assigned Mentor</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Status</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 px-4 py-2 bg-light">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member) => {
                                        const photo = MentorshipService.profilePictureSrc(member.student.profilePicture);
                                        const profileUuid = String(
                                            member.student.uuid || member.studentId || ''
                                        ).trim();
                                        const displayName = member.student.name || '—';
                                        const profileHref =
                                            profileUuid && member.student.name
                                                ? `/student/${profileUuid}/profile`
                                                : null;
                                        return (
                                            <tr key={member.id}>
                                                <td className="px-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(member.id)}
                                                        onChange={() =>
                                                            setSelectedIds((current) =>
                                                                current.includes(member.id)
                                                                    ? current.filter((id) => id !== member.id)
                                                                    : [...current, member.id]
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {photo ? (
                                                            <img
                                                                src={photo}
                                                                alt=""
                                                                width={32}
                                                                height={32}
                                                                className="rounded-circle font-12 font-500"
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                style={{ width: 32, height: 32, fontSize: 12, fontWeight: 500 }}>
                                                                {(member.student.name || '?').slice(0, 1).toUpperCase()}
                                                            </div>
                                                        )}
                                                        {profileHref ? (
                                                            <a
                                                                className="font-13 font-600 text-dark text-decoration-none"
                                                                href={profileHref}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                {displayName}
                                                            </a>
                                                        ) : (
                                                            <span className="font-13 font-600 text-dark">{displayName}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* <td className="font-13">{member.student.uuid || '—'}</td> */}
                                                <td className="font-13">{member.student.programName || '—'}</td>
                                                <td className="font-13">{MentorshipService.displayMenteePoolYear(member.student.batchYear)}</td>
                                                <td className="font-13 px-4">
                                                    {member.tracks
                                                        .map((track) => track.name)
                                                        .filter(Boolean)
                                                        .join(', ') || '—'}{' '}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0"
                                                        onClick={() => {
                                                            setTrackMember(member);
                                                            setTrackIds(member.tracks.map((track) => track.id).filter((id) => id != null));
                                                        }}>
                                                        Edit
                                                    </button>
                                                </td>
                                                <td className="font-13">{MentorshipService.displayMenteePoolAreas(member.preferredMentorshipAreas)}</td>
                                                <td className="font-13">{MentorshipService.displayMenteePoolAssignedMentor(member.assignedMentor)}</td>
                                                <td className="font-13">
                                                    {MentorshipService.displayMenteePoolAssignmentStatus(
                                                        member.confirmationStatus,
                                                        member.assignmentDisplayStatus
                                                    )}
                                                </td>
                                                <td>
                                                    <ActionSettings
                                                        id={member.id}
                                                        actions={['View', 'Status', 'Remove']}
                                                        onClick={handleRowAction}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="card-footer bg-white border-top py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="font-12 text-secondary">
                                Page {page} of {totalPages || 1} · {total} mentees · {selectedIds.length} selected on this view
                            </span>
                            <div className="d-flex gap-2">
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: 90 }}
                                    value={pageSize}
                                    onChange={(event) => loadPage(1, Number(event.target.value))}>
                                    {MENTEE_POOL_PAGE_SIZES.map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled={page <= 1}
                                    onClick={() => loadPage(page - 1, pageSize)}>
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled={page >= (totalPages || 1)}
                                    onClick={() => loadPage(page + 1, pageSize)}>
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <MentorshipQuickMenteeProfile
                open={profileMember != null}
                member={profileMember}
                onClose={() => setProfileMember(null)}
                onEditTracks={() => {
                    if (!profileMember) return;
                    setTrackMember(profileMember);
                    setTrackIds(profileMember.tracks.map((track) => track.id).filter((id) => id != null));
                }}
                onEditStatus={() => {
                    if (!profileMember) return;
                    setStatusMember(profileMember);
                    setStatusValue(profileMember.confirmationStatus || 'Yet to register');
                    setStatusComment(profileMember.confirmationComment || '');
                }}
                onRemove={() => profileMember && setRemoveMember(profileMember)}
                onSendMessage={() => profileMember && sendMessage([profileMember])}
            />

            <MentorshipMenteePoolFileUpload
                show={showFileUpload}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                onClose={() => setShowFileUpload(false)}
                onImported={refresh}
            />

            <MentorshipMenteePoolManualAdd
                show={showManualAdd}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                campuses={campuses}
                tracks={tracks}
                onClose={() => setShowManualAdd(false)}
                onAdded={refresh}
            />

            <Modal show={trackMember != null} onHide={() => setTrackMember(null)} centered style={POOL_MODAL_OVER_OFFCANVAS}>
                <Modal.Header closeButton>
                    <Modal.Title>Career Tracks</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tracks
                        .filter((track) => track.id != null)
                        .map((track) => (
                            <label key={track.id} className="d-block mb-2">
                                <input
                                    type="checkbox"
                                    className="me-2"
                                    checked={trackIds.includes(track.id as number)}
                                    onChange={() =>
                                        setTrackIds((current) =>
                                            current.includes(track.id as number)
                                                ? current.filter((id) => id !== track.id)
                                                : [...current, track.id as number]
                                        )
                                    }
                                />
                                {track.name}
                            </label>
                        ))}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setTrackMember(null)}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-success" disabled={isSaving} onClick={saveTracks}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={statusMember != null} onHide={() => setStatusMember(null)} centered style={POOL_MODAL_OVER_OFFCANVAS}>
                <Modal.Header closeButton>
                    <Modal.Title>Update Confirmation Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label className="form-label">Status</label>
                    <select
                        className="form-select mb-3"
                        value={statusValue}
                        onChange={(event) => setStatusValue(event.target.value)}>
                        {MENTEE_POOL_CONFIRMATION_STATUSES.map((status) => {
                            const optedOutBlocked =
                                status === 'Opted-out' &&
                                statusMember != null &&
                                !allowOptOutFor(statusMember.campusId) &&
                                statusMember.confirmationStatus !== 'Opted-out';
                            return (
                                <option key={status} value={status} disabled={optedOutBlocked}>
                                    {status}
                                </option>
                            );
                        })}
                    </select>
                    <label className="form-label">Comment (optional)</label>
                    <textarea
                        className="form-control"
                        rows={3}
                        value={statusComment}
                        onChange={(event) => setStatusComment(event.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setStatusMember(null)}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-success" disabled={isSaving || !statusValue} onClick={saveStatus}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={removeMember != null} onHide={() => setRemoveMember(null)} centered style={POOL_MODAL_OVER_OFFCANVAS}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove from Pool</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Remove {removeMember?.student.name || removeMember?.student.uuid || 'this mentee'} from the mentee
                    pool? This deletes the membership.
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setRemoveMember(null)}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-danger" disabled={isSaving} onClick={confirmRemove}>
                        Remove
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showBulkRemove} onHide={() => setShowBulkRemove(false)} centered style={POOL_MODAL_OVER_OFFCANVAS}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove from Pool</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Remove {selectedCount} selected mentee{selectedCount === 1 ? '' : 's'} from the mentee pool?
                    This deletes the membership.
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowBulkRemove(false)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        disabled={isSaving}
                        onClick={() => runBulk('remove')}>
                        Remove
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MentorshipMenteePool;
